class ProductBundleV2 extends HTMLElement {
  constructor() {
    super();
    this.onItemClick = this.onItemClick.bind(this);
    this.onItemKeydown = this.onItemKeydown.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onSizeClick = this.onSizeClick.bind(this);
    this.sizes = [];
  }

  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-bundle-item]'));
    this.productInfo = this.closest('product-info');
    this.sizeOptionIndex = Number(this.dataset.sizeOptionIndex ?? -1);
    this.sizeValues = this.parseSizeValues();
    this.sizesInput = this.querySelector('[data-sd-bundle-sizes-input]');

    this.cacheStaticPrices();

    this.items.forEach((item) => {
      item.addEventListener('click', this.onItemClick);
      item.addEventListener('keydown', this.onItemKeydown);
      item.querySelector('[data-sd-bundle-sizes]')?.addEventListener('click', this.onSizeClick);
    });

    if (typeof subscribe === 'function') {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
    }

    this.syncSelectedItemFromForm();
  }

  cacheStaticPrices() {
    this.querySelectorAll('[data-sd-bundle-v2-price], [data-sd-bundle-v2-compare-price]').forEach((element) => {
      element.dataset.sdStaticValue = element.textContent.trim();
    });
  }

  restoreStaticPrices() {
    this.querySelectorAll('[data-sd-bundle-v2-price], [data-sd-bundle-v2-compare-price]').forEach((element) => {
      if (element.dataset.sdStaticValue) {
        element.textContent = element.dataset.sdStaticValue;
      }
    });
  }

  disconnectedCallback() {
    this.items.forEach((item) => {
      item.removeEventListener('click', this.onItemClick);
      item.removeEventListener('keydown', this.onItemKeydown);
      item.querySelector('[data-sd-bundle-sizes]')?.removeEventListener('click', this.onSizeClick);
    });

    this.variantChangeUnsubscriber?.();
  }

  onItemClick(event) {
    this.selectItem(event.currentTarget);
  }

  onItemKeydown(event) {
    if (event.target.closest('[data-sd-bundle-sizes]')) {
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    this.selectItem(event.currentTarget);
  }

  onVariantChange({ data }) {
    if (!data?.variant?.id || String(data.sectionId) !== String(this.sectionId)) {
      return;
    }

    this.restoreStaticPrices();

    const size = this.getVariantSize(data.variant);
    if (size) this.sizes[0] = size;

    this.setSelectedItemByVariantId(String(data.variant.id), false);
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  getCurrentVariantId() {
    const mainFormInput = this.productInfo?.querySelector(
      `#product-form-${this.sectionId} input[name="id"]`
    );

    if (mainFormInput?.value) {
      return mainFormInput.value;
    }

    return this.productInfo?.querySelector('product-form input[name="id"]')?.value;
  }

  getDefaultItem() {
    return this.items.find((item) => item.dataset.bundleIndex === '2') || this.items[0];
  }

  hasUrlVariant() {
    return Boolean(new URLSearchParams(window.location.search).get('variant'));
  }

  syncSelectedItemFromForm() {
    const currentVariantId = this.getCurrentVariantId();

    if (this.hasUrlVariant() && currentVariantId && this.items.some((item) => item.dataset.variantId === currentVariantId)) {
      this.setSelectedItemByVariantId(currentVariantId, false);
      return;
    }

    const defaultItem = this.getDefaultItem();
    if (!defaultItem) {
      return;
    }

    const shouldUpdateProductInfo = currentVariantId !== defaultItem.dataset.variantId;
    this.setSelectedItemByVariantId(defaultItem.dataset.variantId, shouldUpdateProductInfo);
  }

  selectItem(item) {
    if (item.classList.contains('is-selected')) {
      return;
    }

    // Le bloc taille global peut apparaître/disparaître : on garde l'offre cliquée à la même position.
    const previousTop = item.getBoundingClientRect().top;
    this.setSelectedItem(item, true);
    const delta = item.getBoundingClientRect().top - previousTop;
    if (delta) window.scrollBy(0, delta);
  }

  findItemByVariantId(variantId) {
    const directMatch = this.items.find((item) => item.dataset.variantId === String(variantId));
    if (directMatch) return directMatch;

    const variant = this.productInfo?.getVariantById?.(variantId);
    if (!variant || !this.productInfo?.getVariantOptionValues) return null;

    const values = this.productInfo.getVariantOptionValues(variant);
    return this.items.find((item) => {
      const offerIndex = Number(item.dataset.offerOptionIndex);
      return String(values[offerIndex] || '') === String(item.dataset.offerValue || '');
    });
  }

  getOfferVariantId(item) {
    const offerValue = item.dataset.offerValue;
    if (offerValue != null && this.productInfo?.findVariantForOptionChange) {
      const variant = this.productInfo.findVariantForOptionChange(
        Number(item.dataset.offerOptionIndex),
        offerValue
      );
      if (variant?.id) return String(variant.id);
    }

    return item.dataset.variantId;
  }

  setSelectedItemByVariantId(variantId, updateProductInfo = true) {
    const selectedItem = this.findItemByVariantId(variantId);
    if (!selectedItem) return;
    this.setSelectedItem(selectedItem, updateProductInfo);
  }

  setSelectedItem(selectedItem, updateProductInfo = true) {
    this.items.forEach((bundleItem) => {
      const isSelected = bundleItem === selectedItem;
      bundleItem.classList.toggle('is-selected', isSelected);
    });

    this.renderSizes(selectedItem);

    const variantId = updateProductInfo ? this.getOfferVariantId(selectedItem) : selectedItem.dataset.variantId;

    if (updateProductInfo && this.productInfo?.setActiveVariant) {
      this.productInfo.setActiveVariant(variantId);
    }

    const variant = this.productInfo?.getVariantById?.(variantId);
    if (variant) {
      this.productInfo?.updateSubmitButtonPrice?.(variant);
    }

    this.notifyOfferChange(selectedItem, variantId);
  }

  parseSizeValues() {
    const script = this.querySelector('[data-sd-bundle-size-values]');
    if (!script) return [];

    try {
      return JSON.parse(script.textContent) || [];
    } catch {
      return [];
    }
  }

  get hasSizes() {
    return this.sizeOptionIndex >= 0 && this.sizeValues.length > 0;
  }

  get sizeBlock() {
    return this.productInfo?.querySelector('sd-product-size-variants');
  }

  getItemUnits(item) {
    const match = String(item?.dataset.offerValue || '').match(/\d+/);
    return match ? Math.max(1, parseInt(match[0], 10)) : 1;
  }

  getVariantSize(variant) {
    if (!variant || !this.hasSizes) return '';
    return this.productInfo?.getVariantOptionValues?.(variant)[this.sizeOptionIndex] || '';
  }

  getCurrentVariant() {
    return this.productInfo?.getVariantById?.(this.getCurrentVariantId()) || null;
  }

  findVariantForItemSize(item, size) {
    const offerIndex = Number(item.dataset.offerOptionIndex);
    return (
      (this.productInfo?.getProductVariants?.() || []).find((variant) => {
        const values = this.productInfo.getVariantOptionValues(variant);
        return values[offerIndex] === item.dataset.offerValue && values[this.sizeOptionIndex] === size;
      }) || null
    );
  }

  getSelectedItem() {
    return this.items.find((item) => item.classList.contains('is-selected')) || null;
  }

  renderSizes(selectedItem) {
    if (!this.hasSizes) return;

    const units = this.getItemUnits(selectedItem);
    const isMulti = units > 1;

    this.sizeBlock?.classList.toggle('hidden', isMulti);

    if (!this.sizes[0]) {
      this.sizes[0] = this.getVariantSize(this.getCurrentVariant()) || this.sizeValues[0];
    }
    for (let index = 1; index < units; index += 1) {
      if (!this.sizes[index]) this.sizes[index] = this.sizes[0];
    }

    this.items.forEach((item) => {
      const container = item.querySelector('[data-sd-bundle-sizes]');
      if (!container) return;

      if (item !== selectedItem || !isMulti) {
        container.hidden = true;
        delete container.dataset.units;
        container.replaceChildren();
        return;
      }

      container.hidden = false;
      if (container.dataset.units === String(units)) {
        this.refreshSizeRows(item, container);
        return;
      }

      container.dataset.units = String(units);
      container.replaceChildren(
        ...Array.from({ length: units }, (_, unitIndex) => this.buildSizeRow(item, unitIndex))
      );
    });

    this.updateSizesInput(selectedItem);
  }

  refreshSizeRows(item, container) {
    container.querySelectorAll('[data-sd-bundle-size-value]').forEach((button) => {
      const size = button.dataset.sdBundleSizeValue;
      const isSelected = this.sizes[Number(button.dataset.sdBundleSizeUnit)] === size;
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });
  }

  buildSizeRow(item, unitIndex) {
    const row = document.createElement('div');
    row.className = 'sd-product-bundle-v2__size-row';

    const label = document.createElement('p');
    label.className = 'sd-product-bundle-v2__size-label';
    label.id = `SdBundleSize-${this.sectionId}-${item.dataset.bundleIndex}-${unitIndex}`;
    label.textContent = `Culotte ${unitIndex + 1}`;

    const list = document.createElement('div');
    list.className = 'sd-product-bundle-v2__size-list';
    list.setAttribute('role', 'group');
    list.setAttribute('aria-labelledby', label.id);

    this.sizeValues.forEach((size) => {
      const button = document.createElement('button');
      const isSelected = this.sizes[unitIndex] === size;
      const available = this.findVariantForItemSize(item, size)?.available !== false;

      button.type = 'button';
      button.className = 'sd-product-bundle-v2__size-option';
      button.textContent = size;
      button.dataset.sdBundleSizeValue = size;
      button.dataset.sdBundleSizeUnit = String(unitIndex);
      button.classList.toggle('is-selected', isSelected);
      button.classList.toggle('is-unavailable', !available);
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      if (!available) button.setAttribute('aria-disabled', 'true');

      list.appendChild(button);
    });

    row.append(label, list);
    return row;
  }

  onSizeClick(event) {
    const button = event.target.closest('[data-sd-bundle-size-value]');
    if (!button) return;

    event.stopPropagation();
    if (button.getAttribute('aria-disabled') === 'true' || button.classList.contains('is-selected')) return;

    const item = button.closest('[data-bundle-item]');
    const unitIndex = Number(button.dataset.sdBundleSizeUnit);
    const size = button.dataset.sdBundleSizeValue;

    this.sizes[unitIndex] = size;

    button.parentElement.querySelectorAll('[data-sd-bundle-size-value]').forEach((option) => {
      const isSelected = option === button;
      option.classList.toggle('is-selected', isSelected);
      option.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    this.updateSizesInput(item);

    // La variante commandée suit la taille de la 1re culotte ; le détail complet part dans properties[Tailles].
    if (unitIndex === 0) {
      const variant = this.findVariantForItemSize(item, size);
      if (variant?.id && this.productInfo?.setActiveVariant) {
        if (this.sizeBlock) this.sizeBlock.rememberedSize = size;
        this.productInfo.setActiveVariant(variant.id);
      }
    }
  }

  updateSizesInput(selectedItem = this.getSelectedItem()) {
    if (!this.sizesInput) return;

    const units = this.getItemUnits(selectedItem);
    if (units <= 1) {
      this.sizesInput.value = '';
      this.sizesInput.disabled = true;
      return;
    }

    const counts = new Map();
    this.sizes.slice(0, units).forEach((size) => counts.set(size, (counts.get(size) || 0) + 1));

    this.sizesInput.value = this.sizeValues
      .filter((size) => counts.has(size))
      .map((size) => `${counts.get(size)}× ${size}`)
      .join(', ');
    this.sizesInput.disabled = false;
  }

  notifyOfferChange(selectedItem, variantId) {
    const priceEl = selectedItem.querySelector('[data-sd-bundle-v2-price], [data-sd-bundle-price]');

    document.dispatchEvent(
      new CustomEvent('sd:bundle-offer-change', {
        bubbles: true,
        detail: {
          variantId: String(variantId),
          price: priceEl?.dataset.sdStaticValue || priceEl?.textContent.trim() || '',
          available: selectedItem.dataset.variantAvailable !== 'false',
        },
      })
    );
  }
}

if (!customElements.get('sd-product-bundle-v2')) {
  customElements.define('sd-product-bundle-v2', ProductBundleV2);
}
